import { slice } from "lodash";
import { rest } from "msw";
import config from "../../config";
import { searchApiTypes } from "../../modules/aml/aml.constant";
import type {
  AmlBusinessSearchHits,
  AmlDetailsResponse,
  AmlIndividualHitsResponse,
  AmlIndividualSearchHit,
  AmlSearchResult,
  BulkScreeningStatusResponse,
  ProfileKYCAlertById,
} from "../../modules/aml/types/aml.type";
import type {
  AmlAlerts,
  KycAlerts,
  OpenAmlAlertsContainer,
} from "../../modules/profiles/types/alerts.type";
import type {
  AmlSearchesType,
  SearchesKeyPartyType,
} from "../../modules/profiles/types/amlScreen.type";
import type { KeyPartiesType } from "../../modules/profiles/types/profile.type";
import { createUrl } from "../protect-api";
import { apiVersion } from "../../shared/constants/common.constant";
import type { ProblemDetails } from "../../shared/types/problem-details.type";

export const mockGetAmlBusinessDetails = (
  amlBusinessDetails: AmlDetailsResponse
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses/01/hits/05`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200), ctx.json(amlBusinessDetails));
    }
  );

export const mockGetAmlIndividualDetails = (
  amlIndividualDetails: AmlDetailsResponse
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals/01/hits/05`
    ),
    async (_, res, ctx) => {
      return res(ctx.status(200), ctx.json(amlIndividualDetails));
    }
  );

export const mockGetAmlIndividualSearchHits = (
  amlIndividualSearchDetails: AmlIndividualSearchHit[]
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals/960476e0-6982-4b22-863e-78fcc17b8fd7/hits?limit=10&page=1&searchId=960476e0-6982-4b22-863e-78fcc17b8fd7`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlIndividualSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlIndividualSearchDetails.length,
        })
      );
    }
  );
};

export const mockGetAmlBusinessSearchHits = (
  amlBusinessSearchDetails: AmlBusinessSearchHits[]
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses/fec1bb45-6c93-4065-8319-56e159674661/hits?limit=10&page=1&searchId=fec1bb45-6c93-4065-8319-56e159674661`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessSearchDetails.length,
        })
      );
    }
  );
};

export const mockGetAmlSearchResults = (
  searchType: string,
  searchId: string,
  amlBusinessSearchResults: AmlSearchResult[]
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessSearchResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessSearchResults.length,
        })
      );
    }
  );

export const mockGetAmlResults = (
  amlBusinessResults: AmlSearchesType[],
  profileIdMatcher = ":profileId"
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileIdMatcher}/searches`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessResults.length,
        })
      );
    }
  );

export const mockGetAmlScreenKeyPartiesResults = (
  amlKeyPartiesDetails: string[],
  profileId = "960476e0-6982-4b22-863e-78fcc17b8fd7",
  searchId = "960476e0-6982-4b22-863e-78fcc17b8fd7"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/${profileId}/keyParties?limit=15&page=1&searchId=${searchId}`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "15");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlKeyPartiesDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlKeyPartiesDetails.length,
        })
      );
    }
  );
};

export const mockGetKeyPartiesResults = (
  amlKeyPartiesDetails: KeyPartiesType[],
  profileId = "123456789"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlKeyPartiesDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlKeyPartiesDetails.length,
        })
      );
    }
  );
};

export const mockGetAmlAlerts = (
  amlAlerts: AmlAlerts[],
  profileId = ":profileId"
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlAlerts,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlAlerts.length,
        })
      );
    }
  );

export const mockGetKycAlerts = (
  kycAlerts: KycAlerts[],
  profileId = ""
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/${profileId}/alerts`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            kycAlerts,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: kycAlerts.length,
        })
      );
    }
  );

export const mockGetAmlIndividualKeyParties = (
  amlBusinessResults: SearchesKeyPartyType[]
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74/keyparties/searches?type=individual`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessResults.length,
        })
      );
    }
  );

export const mockGetAmlBusinessKeyParties = (
  amlBusinessResults: AmlSearchesType[]
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74/keyparties/searches?type=business`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessResults.length,
        })
      );
    }
  );

export const mockGetAmlIndividualUbos = (
  amlIndividualResults: SearchesKeyPartyType[]
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74/ubos/individual/searches?type=individual`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlIndividualResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlIndividualResults.length,
        })
      );
    }
  );

export const mockGetAmlBusinessKeyUbos = (
  amlBusinessResults: AmlSearchesType[]
): ReturnType<typeof rest.get> =>
  rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74/ubos/business/searches?type=business`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessResults,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessResults.length,
        })
      );
    }
  );

export const mockGetKeyPartiesIndividualAmlSearchHits = (
  amlIndividualSearchDetails: AmlIndividualSearchHit[],
  searchId = "9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/key-parties/individuals/${searchId}/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlIndividualSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlIndividualSearchDetails.length,
        })
      );
    }
  );
};

export const mockGetKeyPartiesBusinessAmlSearchHits = (
  amlBusinessSearchDetails: AmlBusinessSearchHits[],
  id = "fec1bb45-6c93-4065-8319-56e159674661",
  spy?: jest.Mock
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/businesses/${id}/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;
      if (spy) spy(searchParams);

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessSearchDetails.length,
        })
      );
    }
  );
};

export const mockGetUbosIndividualAmlSearchHits = (
  amlIndividualSearchDetails: AmlIndividualSearchHit[],
  searchId = "9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/individuals/${searchId}/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");

      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlIndividualSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlIndividualSearchDetails.length,
        })
      );
    }
  );
};

export const mockGetUbosBusinessAmlSearchHits = (
  amlBusinessSearchDetails: AmlBusinessSearchHits[]
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/ubos/businesses/fec1bb45-6c93-4065-8319-56e159674661/hits`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlBusinessSearchDetails,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlBusinessSearchDetails.length,
        })
      );
    }
  );
};

export const mockUpdateKeyPartiesIndividualAmlSearchHits = (): ReturnType<
  typeof rest.put
> => {
  return rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/key-parties/individuals/9cd4ecf3-db9c-4b22-b64d-0fdc24f54b74/hits`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockUpdateKeyPartiesBusinessAmlSearchHits = (): ReturnType<
  typeof rest.put
> => {
  return rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/key-parties/businesses/fec1bb45-6c93-4065-8319-56e159674661/hits`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockGetAmlSearchIndividualHitsUndecided = (
  hitsResponse: AmlIndividualHitsResponse
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/searches`),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");
      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            hitsResponse.items,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: hitsResponse.count,
        })
      );
    }
  );
};

export const mockGetBulkScreeningStatuses = (
  bulkScreeningResponse: BulkScreeningStatusResponse
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/asyncAmlJobs`),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            bulkScreeningResponse.items,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: bulkScreeningResponse.count,
        })
      );
    }
  );
};

export const mockGetAmlOpenAlerts = (
  amlOpenAlerts: OpenAmlAlertsContainer,
  profileIdMatcher = ""
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileIdMatcher}/amlAlerts`
    ),
    async (request, res, ctx) => {
      const { searchParams } = request.url;

      const pageNumber = parseInt(searchParams.get("page") ?? "1");
      const pageSize = parseInt(searchParams.get("limit") ?? "10");

      return res(
        ctx.status(200),
        ctx.json({
          items: slice(
            amlOpenAlerts.items,
            pageNumber < 1 ? 0 : (pageNumber - 1) * pageSize,
            pageNumber * pageSize
          ),
          count: amlOpenAlerts.count,
        })
      );
    }
  );
};

export const mockGetKYCAlertByAlertId = (
  kycAlert: ProfileKYCAlertById,
  profileIdMatcher = "",
  alertId = kycAlert.id
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/${profileIdMatcher}/alerts/${alertId}`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json(kycAlert));
    }
  );
};

export const mockUpdateKYCAlert = (
  profileId = "",
  alertId = "",
  kycAlert: ProfileKYCAlertById
): ReturnType<typeof rest.put> => {
  return rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/kycMonitoring/profiles/${profileId}/alerts/${alertId}`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({ kycAlert }));
    }
  );
};

export const mockDownloadReport = (
  searchType: string,
  searchId = "",
  returnError = false
): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/download`
    ),
    async (_request, res, ctx) => {
      if (returnError) {
        return res(
          ctx.status(500),
          ctx.json({
            error: "error",
            apiFailed: true,
          })
        );
      } else {
        return res(
          ctx.status(200),
          ctx.json({
            downloadUrl: "https://example.com/download",
            fileName: "example",
            expiresAt: "date",
            apiFailed: false,
          })
        );
      }
    }
  );
};

export const mockDownloadCopyrightReport = (
  searchType: string,
  searchId = "",
  hitId = "",
  sourceId = ""
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}/sources/${sourceId}/copyrighted/download`
    ),
    (_req, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          downloadUrl: "https://example.com/download",
          fileName: "example",
          expiresAt: "date",
          apiFailed: false,
        })
      );
    }
  );
};

export const mockAMLHitDetailDownloadReport = (
  searchType: string,
  hitId: string,
  searchId = "fec1bb45-6c93-4065-8319-56e159674661"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}/download`
    ),
    async (_request, res, ctx) => {
      return res(
        ctx.status(200),
        ctx.json({
          downloadUrl: "https://example.com/download",
          fileName: "example",
          expiresAt: "date",
          apiFailed: false,
        })
      );
    }
  );
};

export const mockGetAsyncAmlJobs = (): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/asyncAmlJobs`),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({ items: [], count: 0 }));
    }
  );
};

export const mockGetKeyPartySearches = (
  profileIdMatcher = "123456"
): ReturnType<typeof rest.get> => {
  return rest.get(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileIdMatcher}/keyparties/searches`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockCheckAmlResultsExceptOpenAlertsTab = (): ReturnType<
  typeof rest.get
> => {
  return rest.get(
    createUrl(`${config.COMPLIANCE_API_URL}/${apiVersion}/`),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({ items: [], count: 0 }));
    }
  );
};

export const mockUseLinkAmlSearchesToProfile = (
  profileId: string
): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/searches/link`
    ),
    async (_request, res, ctx) => {
      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockUpdateAmlSearchHits = (
  searchType: string,
  hitId: string,
  returnError = false,
  spy?: jest.Mock
): ReturnType<typeof rest.put> => {
  return rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${hitId}/hits`
    ),
    async (request, res, ctx) => {
      const requestBody = await request.json();
      if (spy) spy(requestBody);

      if (returnError) {
        return res(ctx.status(500), ctx.json({}));
      } else {
        return res(ctx.status(200), ctx.json({}));
      }
    }
  );
};

export const mockUpdateAmlAlertHit = (
  searchType: string,
  searchId: string,
  hitId: string,
  returnError = false,
  spy?: jest.Mock
): ReturnType<typeof rest.put> => {
  return rest.put(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}/${searchId}/hits/${hitId}`
    ),
    async (request, res, ctx) => {
      const requestBody = await request.json();
      if (spy) spy(requestBody);

      if (returnError) {
        return res(ctx.status(500), ctx.json({}));
      } else {
        return res(ctx.status(200), ctx.json({}));
      }
    }
  );
};

export const mockAddAmlSearch = (
  searchType: string,
  spy?: jest.Mock,
  isErrorResponse?: {
    returnError: boolean;
    errorResponseObject: ProblemDetails;
  }
): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searches/${searchType}`
    ),
    async (request, res, ctx) => {
      const requestBody = await request.json();
      if (spy) spy(requestBody);

      if (isErrorResponse?.returnError) {
        return res(ctx.status(400), ctx.json({}));
      }
      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockLinkKeyPartiesUboAmlSearchesToProfile = (
  profileId: string,
  spy?: jest.Mock
): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/searches/link`
    ),
    async (request, res, ctx) => {
      const requestBody = await request.json();

      if (spy) {
        spy(requestBody);
      }

      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockBulkLinkAmlSearchesToKeyParties = (
  profileId: string,
  spy?: jest.Mock
): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/profiles/${profileId}/keyparties/searches/bulk`
    ),
    async (request, res, ctx) => {
      const requestBody = await request.json();

      if (spy) {
        spy(requestBody);
      }

      return res(ctx.status(200), ctx.json({}));
    }
  );
};

export const mockAMLHitDetailsReport = ({
  searchType,
  supplierHitId,
  returnError = false,
}: {
  searchType: string;
  supplierHitId: string;
  returnError?: boolean;
}): ReturnType<typeof rest.post> => {
  return rest.post(
    createUrl(
      `${config.COMPLIANCE_API_URL}/${apiVersion}/searchLinks/${searchType}/${supplierHitId}/download`,
      true
    ),
    async (_req, res, ctx) => {
      if (returnError) {
        return res(
          ctx.status(500),
          ctx.json({
            error: "error",
            apiFailed: true,
          })
        );
      }

      const reportType =
        searchType === searchApiTypes.business ? "business" : "individual";

      const mockedFileName = `${reportType}-report-${supplierHitId}.pdf`;
      return res(
        ctx.status(200),
        ctx.json({
          downloadUrl: `/mock-downloads/${mockedFileName}`,
          fileName: mockedFileName,
          expiresAt: "2024-12-31T23:59:59Z",
          apiFailed: false,
        })
      );
    }
  );
};

export const mockServeMockDownloads = (): ReturnType<typeof rest.get> =>
  rest.get(`/mock-downloads/:fileName`, async (req, res, ctx) => {
    const { fileName } = req.params as { fileName: string };
    const fileContent = `Mock content for ${fileName}`;

    return res(
      ctx.status(200),
      ctx.set("Content-Type", "application/pdf"),
      ctx.set("Content-Disposition", `attachment; filename="${fileName}"`),
      ctx.body(fileContent)
    );
  });

export const mockDownloadBusinessHitReport = (
  supplierHitId: string
): ReturnType<typeof rest.post> =>
  mockAMLHitDetailsReport({
    searchType: searchApiTypes.business,
    supplierHitId,
  });

export const mockDownloadIndividualHitReport = (
  supplierHitId: string
): ReturnType<typeof rest.post> =>
  mockAMLHitDetailsReport({
    searchType: searchApiTypes.individual,
    supplierHitId,
  });
